'use client';

import { useCallback, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER,
  getSignalStatusOptions,
  resolveSignalsStatusFilterBadgeText,
} from '@terra/shared-contracts-signals';

const STATUS_KEY_PREFIX = 'status.';
const BLOCKED_REASON_KEY_PREFIX = 'status.blockedReason.';
const CONFIRMED_FINDING_STATE_KEYS = new Set<string>([
  `${STATUS_KEY_PREFIX}${CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.OPEN}`,
  `${STATUS_KEY_PREFIX}${CONFIRMED_VULNERABILITY_FINDING_STATE_STATUS_FILTER.CLOSED}`,
]);

export function useSignalStatusFilterTranslations() {
  const tFilter = useTranslations('designSystem.filter');
  const tSignalsFilters = useTranslations('signals.filters');
  const tLifecycleStatus = useTranslations(
    'designSystem.signals.lifecycle.status',
  );
  const tStatusFilterTooltips = useTranslations(
    'designSystem.signals.lifecycle.status.filterTooltips',
  );
  const tStatusFilter = useCallback(
    (key: string, params?: Record<string, unknown>) => {
      if (key === 'status.blockedReasonCount') {
        return tSignalsFilters('blockedReasonCount', {
          count: Number(params?.count ?? 0),
        });
      }

      if (CONFIRMED_FINDING_STATE_KEYS.has(key)) {
        return tSignalsFilters(
          `statusLabels.${key.slice(STATUS_KEY_PREFIX.length)}` as never,
        );
      }

      if (key.startsWith(BLOCKED_REASON_KEY_PREFIX)) {
        return tLifecycleStatus(
          `blockedReason.${key.slice(BLOCKED_REASON_KEY_PREFIX.length)}` as never,
        );
      }

      if (key.startsWith(STATUS_KEY_PREFIX)) {
        return tLifecycleStatus(key.slice(STATUS_KEY_PREFIX.length) as never);
      }

      return key;
    },
    [tLifecycleStatus, tSignalsFilters],
  );
  const statusOptions = useMemo(
    () =>
      getSignalStatusOptions({
        t: tStatusFilter,
        tFilterTooltip: (statusKey) =>
          tStatusFilterTooltips(statusKey as never),
      }),
    [tStatusFilter, tStatusFilterTooltips],
  );
  const resolveStatusBadgeText = useCallback(
    ({
      selectedValues,
      t = tStatusFilter,
    }: {
      selectedValues: readonly string[];
      t?: (key: string, params?: Record<string, unknown>) => string;
    }) =>
      resolveSignalsStatusFilterBadgeText({
        selectedValues,
        t,
        tSelected: () => tFilter('selected'),
      }),
    [tFilter, tStatusFilter],
  );

  return { tStatusFilter, statusOptions, resolveStatusBadgeText };
}
